import { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DateTime } from 'luxon'
import { getUserId } from 'utils/common'
import { ACCENT_BLUE } from 'utils/brand-colors'
import {
  useCancelSubscriptionMutation,
  useCompleteCheckoutMutation,
  useCreateCheckoutSessionMutation,
  useGetSubscriptionByUserIdQuery,
  useReactivateSubscriptionMutation,
  useUpdateSubscriptionQuantityMutation,
} from 'store/subscriptions/subscriptions.api'
import {
  DEFAULT_DRIVER_UNIT_PRICE,
  formatCadAmount,
  hasActiveSubscription,
  isCancelScheduled,
} from 'store/subscriptions/subscriptions.types'
import DriversPlanIcon from 'components/subscription/drivers-plan-icon.component'
import UpgradeSubscriptionDialog from 'components/subscription/upgrade-subscription-dialog.component'
import SubscribeDriversDialog from 'components/subscription/subscribe-drivers-dialog.component'
import StripeCheckoutDialog from 'components/subscription/stripe-checkout-dialog.component'
import CancelSubscriptionDialog from 'components/subscription/cancel-subscription-dialog.component'
import SettingsBillingInvoices from './settings-billing-invoices.component'

const NAVY = '#12355b'
const MUTED = '#6b7280'
const CARD_BLUE = '#eef6fb'
const BORDER = '#d7e3ee'

const formatBillingDate = (value?: string | null) => {
  if (!value) return null
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toFormat('MMM dd, yyyy') : null
}

const nextBillingDate = (createdAt?: string | null, cancelDate?: string | null) => {
  const fromCancel = formatBillingDate(cancelDate)
  if (fromCancel) return fromCancel
  if (!createdAt) return '—'
  const created = DateTime.fromISO(createdAt)
  if (!created.isValid) return '—'
  return created.plus({ months: 1 }).toFormat('MMM dd, yyyy')
}

const SettingsBilling = () => {
  const userId = getUserId()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [publishableKey, setPublishableKey] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [subscribeError, setSubscribeError] = useState<string | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [tab, setTab] = useState(0)
  const [invoiceYear, setInvoiceYear] = useState(DateTime.now().year)
  const sessionIdRef = useRef<string | null>(null)

  const { data: subscription, isLoading, isError, refetch } =
    useGetSubscriptionByUserIdQuery({ userId }, { skip: !userId })
  const [createCheckout, { isLoading: creatingCheckout }] =
    useCreateCheckoutSessionMutation()
  const [completeCheckout] = useCompleteCheckoutMutation()
  const [updateQuantity, { isLoading: updatingQuantity }] =
    useUpdateSubscriptionQuantityMutation()
  const [cancelSubscription, { isLoading: cancelling }] =
    useCancelSubscriptionMutation()
  const [reactivateSubscription, { isLoading: reactivating }] =
    useReactivateSubscriptionMutation()

  const hasPlan = hasActiveSubscription(subscription)
  const unit = subscription?.unit ?? 0
  const unitPrice =
    subscription?.unitPrice && subscription.unitPrice > 0
      ? Number(subscription.unitPrice)
      : DEFAULT_DRIVER_UNIT_PRICE
  const amount = unit * unitPrice
  const driversAdded = subscription?.numberOfDrivers ?? 0
  const cancelScheduled = isCancelScheduled(subscription)
  const canResubscribe = Boolean(subscription?.id) && !hasPlan
  const status = 'Active'

  const planSummary = useMemo(
    () => ({
      nextBilling: nextBillingDate(
        subscription?.createdAt,
        subscription?.cancelDate
      ),
    }),
    [subscription?.cancelDate, subscription?.createdAt]
  )

  const handleContinueSubscription = async (selectedUnit: number) => {
    if (!userId || !Number.isInteger(selectedUnit) || selectedUnit < 1) return
    setSubscribeError(null)
    try {
      const result = await createCheckout({ userId, unit: selectedUnit }).unwrap()
      if (!result.clientSecret) {
        setSubscribeError('Unable to start checkout. Please try again.')
        return
      }
      setSessionId(result.sessionId)
      sessionIdRef.current = result.sessionId
      setClientSecret(result.clientSecret)
      setPublishableKey(result.publishableKey || null)
      setSubscribeOpen(false)
      setCheckoutOpen(true)
    } catch {
      setSubscribeError('Unable to start checkout. Please try again.')
    }
  }

  const handleCheckoutComplete = async () => {
    const completedSessionId = sessionIdRef.current || sessionId
    if (!completedSessionId) return
    try {
      await completeCheckout({ sessionId: completedSessionId }).unwrap()
      await refetch()
      setCheckoutOpen(false)
      setClientSecret(null)
      setSessionId(null)
      sessionIdRef.current = null
    } catch {
      setCheckoutOpen(false)
      setSubscribeOpen(true)
      setSubscribeError(
        'Payment finished, but we could not save the subscription. Please try again.'
      )
    }
  }

  const handleCheckoutClose = () => {
    setCheckoutOpen(false)
    setClientSecret(null)
    setSessionId(null)
    sessionIdRef.current = null
    setSubscribeOpen(true)
  }

  const handleUpdateQuantity = async (nextUnit: number) => {
    if (!userId) return
    setUpdateError(null)
    try {
      await updateQuantity({ userId, unit: nextUnit }).unwrap()
      setUpgradeOpen(false)
    } catch {
      setUpdateError('Unable to update subscription. Please try again.')
    }
  }

  const handleReactivate = async (nextUnit: number) => {
    if (!userId) return
    setUpdateError(null)
    try {
      await reactivateSubscription({ userId, unit: nextUnit }).unwrap()
      setUpgradeOpen(false)
    } catch {
      setUpdateError('Unable to reactivate subscription. Please try again.')
    }
  }

  const handleCancelSubscription = async () => {
    if (!userId) return
    setCancelError(null)
    try {
      await cancelSubscription({ userId }).unwrap()
      setCancelOpen(false)
    } catch {
      setCancelError('Unable to cancel subscription. Please try again.')
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 2, sm: 3 }, pb: 2 }}>
        <Typography variant="h5" fontWeight={700} color={NAVY}>
          Subscription Plan
        </Typography>
        <Typography color={MUTED} mt={0.5} mb={1.5}>
          Manage your subscription and driver limits.
        </Typography>
        <Tabs
          value={tab}
          onChange={(_event, nextTab) => setTab(nextTab)}
          sx={{
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 40,
              color: MUTED,
            },
            '& .Mui-selected': {
              color: `${ACCENT_BLUE} !important`,
              fontWeight: 700,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: ACCENT_BLUE,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Subscription" />
          <Tab label="Invoices" />
        </Tabs>

        {tab === 1 && (
          <SettingsBillingInvoices
            userId={userId}
            createdAt={subscription?.createdAt}
            year={invoiceYear}
            onYearChange={setInvoiceYear}
          />
        )}

        {tab === 0 && isLoading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={28} />
      </Box>
        )}

        {tab === 0 && isError && (
          <Typography color="error">
            Unable to load subscription details. Please try again.
          </Typography>
        )}

        {tab === 0 && !isLoading && !isError && !hasPlan && (
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '16px',
              bgcolor: '#fff',
              overflow: 'hidden',
              minHeight: 360,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 4,
                py: 6,
              }}
            >
              <Typography fontWeight={700} fontSize={22} color={NAVY}>
                {canResubscribe
                  ? 'Subscription Canceled'
                  : 'No Active Subscription'}
              </Typography>
              <Typography mt={1} color={MUTED} maxWidth={420}>
                {canResubscribe
                  ? 'Your previous subscription has ended. Resubscribe to keep adding drivers and accessing all features.'
                  : "You don't have an active subscription yet. Subscribe to start adding drivers and accessing all features."}
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: CARD_BLUE,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                px: 3,
                py: 2.5,
              }}
            >
              <Box
                sx={{
                  pr: { sm: 3 },
                  borderRight: { sm: `1px solid ${BORDER}` },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <Typography color={MUTED} fontSize={13}>
                  Price per Driver
                </Typography>
                <Typography fontWeight={700} fontSize={22} color={NAVY} mt={0.5}>
                  CAD {formatCadAmount(DEFAULT_DRIVER_UNIT_PRICE)}
                </Typography>
                <Typography color={MUTED} fontSize={13}>
                  per month
                </Typography>
              </Box>
              <Box
                sx={{
                  px: { sm: 3 },
                  borderRight: { sm: `1px solid ${BORDER}` },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <Typography color={MUTED} fontSize={13}>
                  Flexible Plan
                </Typography>
                <Typography color={NAVY} mt={0.75} fontSize={15}>
                  Add or remove drivers anytime
                </Typography>
              </Box>
              <Box sx={{ pl: { sm: 3 } }}>
                <Typography color={MUTED} fontSize={13}>
                  Monthly Billing
                </Typography>
                <Typography color={NAVY} mt={0.75} fontSize={15}>
                  Billed monthly
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {tab === 0 && !isLoading && !isError && hasPlan && (
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '16px',
              overflow: 'hidden',
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                bgcolor: CARD_BLUE,
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <DriversPlanIcon size={56} />
              <Box>
                <Typography color={MUTED} fontSize={13}>
                  Current Plan
                </Typography>
                <Typography fontWeight={700} fontSize={22} color={NAVY}>
                  Driver Plan
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: '999px',
                    bgcolor: '#dcfce7',
                    color: '#166534',
                      fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: '#22c55e',
                    }}
                  />
                  {status}
                </Box>
              </Box>
            </Box>

            <Box px={3} py={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Item', 'Quantity', 'Unit Price (CAD)', 'Amount (CAD)'].map(
                      (header) => (
                        <TableCell
                          key={header}
                          sx={{
                            color: MUTED,
                            borderBottom: `1px solid ${BORDER}`,
                            fontWeight: 500,
                            pl: 0,
                          }}
                        >
                          {header}
                  </TableCell>
                      )
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                  <TableRow>
                    <TableCell sx={{ pl: 0, borderBottom: 'none', fontWeight: 600, color: NAVY }}>
                      Drivers
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600, color: NAVY }}>
                      {unit}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600, color: NAVY }}>
                      {formatCadAmount(unitPrice)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600, color: NAVY }}>
                      {formatCadAmount(amount)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
                mt={1}
                mb={1}
              >
                <Typography fontWeight={700} color={NAVY}>
                  Total (Monthly)
                </Typography>
                <Box
                        sx={{
                    bgcolor: CARD_BLUE,
                    px: 2.5,
                    py: 1,
                    borderRadius: '10px',
                    minWidth: 130,
                    textAlign: 'right',
                  }}
                >
                  <Typography fontWeight={700} fontSize={20} color={NAVY}>
                    CAD {formatCadAmount(amount)}
                          </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
                            sx={{
                bgcolor: CARD_BLUE,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                px: 3,
                py: 2.5,
              }}
            >
              <Box
                              sx={{
                  pr: { sm: 3 },
                  borderRight: { sm: `1px solid ${BORDER}` },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <GroupsOutlinedIcon sx={{ color: ACCENT_BLUE }} />
                <Typography color={MUTED} fontSize={13} mt={0.5}>
                  Drivers Added
                </Typography>
                <Typography fontWeight={700} fontSize={22} color={NAVY}>
                  {driversAdded} of {unit}
                </Typography>
              </Box>
              <Box sx={{ pl: { sm: 3 } }}>
                <CalendarMonthOutlinedIcon sx={{ color: ACCENT_BLUE }} />
                <Typography color={MUTED} fontSize={13} mt={0.5}>
                  Next Billing Date
                </Typography>
                <Typography fontWeight={700} fontSize={22} color={NAVY}>
                  {planSummary.nextBilling}
                          </Typography>
              </Box>
            </Box>
          </Box>
          )}
        </Box>

      {tab === 0 && !isLoading && !isError && (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: 1,
          }}
        >
          {hasPlan && (
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                bgcolor: CARD_BLUE,
                borderRadius: '12px',
                p: 2,
                textAlign: 'left',
                mb: 2,
              }}
            >
              <InfoOutlinedIcon sx={{ color: ACCENT_BLUE, mt: '2px' }} />
            <Box>
                <Typography fontWeight={700} color={NAVY}>
                  {cancelScheduled
                    ? 'Your subscription is active until the end of this billing period.'
                    : 'Your subscription is active.'}
              </Typography>
                <Typography color={NAVY} mt={0.5}>
                  You are currently subscribed for <b>{unit}</b>{' '}
                  {unit === 1 ? 'driver' : 'drivers'} at{' '}
                  <b>CAD {formatCadAmount(unitPrice)}</b> per driver per month
                  (Total <b>CAD {formatCadAmount(amount)}</b>/month).
                  {cancelScheduled
                    ? ` It remains active until ${planSummary.nextBilling}. You will not be charged from the next billing cycle.`
                    : ' You can update the number of drivers at any time. Changes will be applied from your next billing cycle.'}
              </Typography>
            </Box>
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            {hasPlan && cancelScheduled && (
              <Button
                variant="contained"
                onClick={() => {
                  setUpdateError(null)
                  setUpgradeOpen(true)
                }}
                sx={{ px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
              >
                Reactivate
              </Button>
            )}
            {hasPlan && !cancelScheduled && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setCancelError(null)
                    setCancelOpen(true)
                  }}
                  sx={{ px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
                >
                  Cancel Subscription
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setUpdateError(null)
                    setUpgradeOpen(true)
                  }}
                  sx={{ px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
                >
                  Update Subscription
                </Button>
              </>
            )}
            {!hasPlan && (
              <Button
            variant="contained"
                onClick={() => {
                  setSubscribeError(null)
                  setSubscribeOpen(true)
                }}
                sx={{ px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
              >
                {canResubscribe ? 'Resubscribe' : 'Subscribe Now'}
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <SubscribeDriversDialog
        open={subscribeOpen}
        initialUnit={unit > 0 ? unit : 1}
        resubscribe={canResubscribe}
        submitting={creatingCheckout}
        errorMessage={subscribeError}
        onClose={() => setSubscribeOpen(false)}
        onContinue={handleContinueSubscription}
      />
      <UpgradeSubscriptionDialog
        open={upgradeOpen}
        currentUnits={unit}
        numberOfDrivers={driversAdded}
        unitPrice={unitPrice}
        mode={cancelScheduled ? 'reactivate' : 'update'}
        submitting={cancelScheduled ? reactivating : updatingQuantity}
        errorMessage={updateError}
        onClose={() => setUpgradeOpen(false)}
        onConfirm={cancelScheduled ? handleReactivate : handleUpdateQuantity}
      />
      <CancelSubscriptionDialog
        open={cancelOpen}
        nextBillingDate={planSummary.nextBilling}
        submitting={cancelling}
        errorMessage={cancelError}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancelSubscription}
      />
      <StripeCheckoutDialog
        open={checkoutOpen}
        publishableKey={publishableKey}
        clientSecret={clientSecret}
        onClose={handleCheckoutClose}
        onComplete={handleCheckoutComplete}
      />
            </Box>
  )
}

export default SettingsBilling
